import { Sprite, SpriteMaterial, BoxHelper } from 'three';

const randomBetween = (min, max) => min + Math.random() * (max - min);

// intensity is usually a { min, max } range (same shape GAME.calculateWindForce resolves),
// but config's own default is a plain number before any level has loaded — handle both.
// phase/speedFactor let each cloud drift out of sync with the others instead of all
// gusting and easing in lockstep.
function cloudWindX(GAME, phase, speedFactor) {
    const { direction, intensity } = GAME.config.wind;
    const sign = direction === 'l' ? -1 : 1;
    const min = typeof intensity === 'number' ? intensity : (intensity?.min ?? 0);
    const max = typeof intensity === 'number' ? intensity : (intensity?.max ?? 0);
    const time = GAME.TIME.elapsed * 0.001 * speedFactor + phase;
    const smoothRandom = (Math.sin(time * 0.5) + Math.sin(time * 0.3) * 0.5) * 0.5 + 0.5;
    const smoothIntensity = min + (max - min) * smoothRandom;
    return sign * smoothIntensity;
}

export default class Cloud {
    constructor(GAME, texture, { scale = 1, opacity = 1, x = 0, y = 0, z = 0, altitude, depth, debug = false, renderOrder = 0 } = {}) {
        this.GAME = GAME;
        this.baseOpacity = opacity;
        this.aspect = texture.image.width / texture.image.height;
        this.isRepositioning = false;
        this.altitudeBounds = altitude || GAME.config.clouds.altitude;
        this.depthBounds = depth || GAME.config.clouds.depth;
        this.windPhase = randomBetween(0, Math.PI * 2 * 100);
        this.windSpeedFactor = randomBetween(0.8, 1.2);

        const groundHalfWidth = GAME.config.ground.width / 2;
        this.bounds = {
            min: -groundHalfWidth - 20,
            max: groundHalfWidth + 20,
        };

        const material = new SpriteMaterial({ map: texture, transparent: true, depthWrite: false, opacity });
        this.mesh = new Sprite(material);
        this.setScale(scale);
        this.mesh.position.set(x, y, z);
        // The logo's glTF globe material is also transparent/depthWrite:false, so it
        // shares this render bucket with cloud sprites; without an explicit renderOrder
        // the two get sorted unreliably and distant clouds can paint over the logo.
        this.mesh.renderOrder = renderOrder;

        if (debug) this.debugHelper = new BoxHelper(this.mesh, 0xff0000);

        this.GAME.TIME.addEventListener('tick', this.onTick.bind(this));
    }

    setScale(scale) {
        this.mesh.scale.set(scale * this.aspect, scale, 1);
    }

    setOpacity(opacity) {
        this.mesh.material.opacity = opacity;
    }

    onTick() {
        const windSpeed = cloudWindX(this.GAME, this.windPhase, this.windSpeedFactor) * this.GAME.TIME.delta / 1000;
        this.update(windSpeed);
        this.debugHelper?.update();
    }

    update(windSpeed) {
        if (this.isRepositioning) return;

        const offset = windSpeed * this.GAME.config.clouds.shear;
        this.mesh.position.x += offset;

        if (this.mesh.position.x > this.bounds.max || this.mesh.position.x < this.bounds.min) {
            this.reset(offset > 0 ? this.bounds.min : this.bounds.max);
        }
    }

    reset(newX) {
        this.isRepositioning = true;

        const { altitudeBounds: altitude, depthBounds: depth } = this;

        this.fade(this.mesh.material.opacity, 0, 300, () => {
            this.mesh.position.x = newX;
            this.mesh.position.y = randomBetween(altitude.min, altitude.max);
            this.mesh.position.z = randomBetween(depth.min, depth.max);

            // Resume drifting immediately so the cloud is already moving
            // as it fades back in, instead of appearing to start-stop.
            this.isRepositioning = false;

            this.fade(0, this.baseOpacity, 400);
        });
    }

    fade(from, to, duration, onComplete) {
        const start = performance.now();

        const animate = (now) => {
            const t = Math.min((now - start) / duration, 1);

            this.setOpacity(from + (to - from) * t);

            if (t < 1) {
                requestAnimationFrame(animate);
            } else {
                onComplete?.();
            }
        };

        requestAnimationFrame(animate);
    }

    static createField(GAME, textures, config = GAME.config.clouds) {
        const { count, scale, opacity, altitude, depth, debug, renderOrder } = config;
        const groundHalfWidth = GAME.config.ground.width / 2;
        const xBounds = { min: -groundHalfWidth - 20, max: groundHalfWidth + 20 };

        return Array.from({ length: count }, () => new Cloud(GAME, textures[Math.floor(Math.random() * textures.length)], {
            scale: randomBetween(scale.min, scale.max),
            opacity: randomBetween(opacity.min, opacity.max),
            x: randomBetween(xBounds.min, xBounds.max),
            y: randomBetween(altitude.min, altitude.max),
            z: randomBetween(depth.min, depth.max),
            debug,
            renderOrder,
        }));
    }

    // Recreates the original hand-placed low-poly cloud layout (cloud1.glb-cloud10.glb)
    // as sprites, so clouds stay visible once the gameplay camera zooms in.
    static createFixedField(GAME, textures, { layout, altitude, depth }) {
        return layout.map(({ x, y, z, scale }) => new Cloud(GAME, textures[Math.floor(Math.random() * textures.length)], {
            scale,
            opacity: 1,
            x,
            y,
            z,
            altitude,
            depth,
        }));
    }
}
